import { useEffect, useMemo, useState } from "react";
import { useLocation, useRoute } from "wouter";
import { Plus } from "lucide-react";
import AppShell from "@/components/foundations/core/AppShell/AppShell";
import { Button } from "@/components/ui/button";
import { trpc } from "@/lib/trpc";
import { DEFAULT_AREA_COLOR, AREA_COLORS, ListColorKey } from "@/lib/listColors";
import PatternAddSection, {
  createPatternAddSection,
  patternSectionToSelection,
  PatternAddSectionValue,
} from "@/components/tasks/patterns/PatternAddSection/PatternAddSection";
import {
  formatDateLong,
  nowTimeString,
  parseDateKey,
} from "@/lib/time";
import { toast } from "sonner";

export default function ListToDay() {
  const [, params] = useRoute<{ date: string; listId: string }>(
    "/day/:date/add/:listId",
  );
  const [, setLocation] = useLocation();
  const date = params?.date ?? "";
  const listId = params ? parseInt(params.listId, 10) : 0;
  const utils = trpc.useUtils();

  const listQuery = trpc.lists.get.useQuery({ id: listId }, { enabled: listId > 0 });
  const templatesQuery = trpc.templates.list.useQuery(
    { listId },
    { enabled: listId > 0 },
  );

  const [sections, setSections] = useState<PatternAddSectionValue[]>([]);

  useEffect(() => {
    if (!templatesQuery.data) return;
    const fallback = nowTimeString();
    setSections(
      templatesQuery.data.map(t => createPatternAddSection(t, fallback)),
    );
  }, [templatesQuery.data]);

  const updateSection = (id: number, next: PatternAddSectionValue) =>
    setSections(s => s.map(x => (x.templateId === id ? next : x)));

  const addMutation = trpc.day.addListToDay.useMutation({
    onSuccess: () => {
      utils.day.list.invalidate({ date });
      utils.day.monthSummary.invalidate();
      toast.success("Tasks added to day");
      setLocation(`/calendar?day=${date}`);
    },
  });

  const selectedCount = sections.filter(s => s.selected).length;

  function addBlankTask() {
    alert('need to do this still')
  }

  function handleAdd() {
    const fallbackStartTime = nowTimeString();
    const selections = sections
      .filter(s => s.selected)
      .map(patternSectionToSelection);
    if (selections.length === 0) return;
    addMutation.mutate({
      date,
      listId,
      fallbackStartTime,
      selections,
    });
  }

  const areaCol = useMemo(() => {
    return AREA_COLORS[(listQuery.data?.color as ListColorKey) ?? DEFAULT_AREA_COLOR];
  }, [listQuery.data?.color]);

  const dateLabel = date ? formatDateLong(parseDateKey(date)) : "";

  return (
    <AppShell backTo={`/day/${date}/add`} title={listQuery.data?.name ?? "List"}>
      <div className="container max-w-2xl py-8 space-y-6 pb-32">
        <header className="space-y-2">
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
            {dateLabel}
          </p>
          <div className="flex items-center gap-3">
            <span
              className="h-9 w-9 rounded-full flex items-center justify-center font-display text-sm"
              style={{ background: areaCol.soft, color: areaCol.ink }}
            >
              {listQuery.data?.priorityNumber ?? "—"}
            </span>
            <h1 className="font-display text-3xl tracking-tight">
              {listQuery.data?.name}
            </h1>
          </div>
          {listQuery.data?.description && (
            <p className="text-sm text-muted-foreground max-w-prose">
              {listQuery.data.description}
            </p>
          )}
        </header>

        {templatesQuery.isLoading ? (
          <div className="space-y-2">
            {[0, 1, 2].map(i => (
              <div key={i} className="h-20 rounded-xl bg-muted/40 animate-pulse" />
            ))}
          </div>
        ) : sections.length === 0 ? (
          <div className="border border-dashed border-border rounded-xl p-10 text-center space-y-3">
            <p className="font-display text-lg">This list has no patterns yet</p>
            <p className="text-sm text-muted-foreground">
              Add patterns in settings, then come back to schedule them.
            </p>

            <Button size="sm" onClick={() => setSections([...sections, { templateId: 0, selected: false, expanded: true, form: { title: "", details: "", startTime: nowTimeString(), durationMin: "60", alertEnabled: false, priority: null, customColors: false, barColor: null, bgColor: null } }])} className="gap-1.5">
              <Plus className="h-4 w-4" />
              Add Blank Task
            </Button>
            <Button
              variant="outline"
              onClick={() => setLocation(`/settings/lists/${listId}`)}
            >
              Create a pattern
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {sections.map(s => {
              return (
                <PatternAddSection
                  key={s.templateId}
                  value={s}
                  onChange={next => updateSection(s.templateId, next)}
                  listName={listQuery.data?.name ?? "List"}
                  listColor={listQuery.data?.color ?? DEFAULT_AREA_COLOR}
                  listBgColor={listQuery.data?.bgColor ?? null}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* Floating "Add" bar */}
      <div className="fixed bottom-0 inset-x-0 border-t border-border/60 bg-background/90 backdrop-blur z-30 pb-safe">
        <div className="container max-w-2xl py-3 flex items-center justify-between gap-3">
          <span className="text-sm text-muted-foreground">
            {selectedCount} {selectedCount === 1 ? "task" : "tasks"} selected
          </span>
          <Button
            size="lg"
            disabled={selectedCount === 0 || addMutation.isPending}
            onClick={handleAdd}
          >
            Add to day
          </Button>
        </div>
      </div>
    </AppShell>
  );
}
